/**
 * useSSE — Singleton SSE manager for real-time events.
 *
 * Usage:
 *   import { sseManager, useSSEEvent } from '@/hooks/useSSE'
 *
 *   // Connect once after login:
 *   sseManager.connect(token)
 *
 *   // Subscribe to events in a component:
 *   useSSEEvent('notification', (event) => { ... })
 */
import { useEffect, useRef } from 'react'
import { apiBaseUrl } from '@/lib/api'

export type SSEEvent = { type: string; payload: unknown; ts: string }
type Handler = (event: SSEEvent) => void

class SSEManager {
  private es: EventSource | null = null
  private handlers = new Map<string, Set<Handler>>()
  private token: string | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  connect(token: string) {
    if (this.es && this.token === token) return
    this.token = token
    if (this.es) {
      this.es.close()
      this.es = null
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this._open()
  }

  private _open() {
    if (!this.token) return
    // apiBaseUrl prefixes the production host on Capacitor native builds —
    // a bare relative path resolves against capacitor://localhost, which
    // doesn't exist (see guide-mobile-ux.md's native-fetch rule).
    const url = `${apiBaseUrl}/api/v1/events/stream?token=${encodeURIComponent(this.token)}`
    this.es = new EventSource(url)

    this.es.onmessage = (e) => {
      try {
        const event: SSEEvent = JSON.parse(e.data)
        this.handlers.get(event.type)?.forEach(h => h(event))
      } catch {
        // ignore malformed events
      }
    }

    this.es.onerror = () => {
      // Browser handles reconnect automatically for transient errors.
      // If the connection is truly closed, we let the browser retry.
    }
  }

  subscribe(type: string, handler: Handler): () => void {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set())
    this.handlers.get(type)!.add(handler)
    return () => this.handlers.get(type)?.delete(handler)
  }

  disconnect() {
    if (this.es) {
      this.es.close()
      this.es = null
    }
    this.token = null
  }
}

// Singleton — shared across all consumers in the app
export const sseManager = new SSEManager()

/**
 * useSSEEvent — subscribe to a specific SSE event type.
 * Handler is stable via ref — no re-subscription on render.
 */
export function useSSEEvent(type: string, handler: Handler) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    return sseManager.subscribe(type, (e) => handlerRef.current(e))
  }, [type])
}
